import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, InputNumber, Radio, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  formatMoney,
  getEditableSlot,
  getStore,
  ISSUE_META,
  rowHasDiscrepancy,
  SHOPIFY_COMPARED_STORE,
  SHOPIFY_READONLY_STORE,
} from './helpers';

const { Text } = Typography;

/** Mirrors backend FIELD_LABELS (fallback when a row arrives without `label`). */
export const FIELD_LABELS = {
  rrp_unit: 'RRP Unit',
  rrp_pack: 'RRP Pack',
  rrp_case: 'RRP Case',
  price_on: 'On Prem',
  price_off: 'Off Prem',
  price_progressive: 'Progressive',
  price_au: 'AU',
  price_vic_sa: 'VIC/SA',
  price_qld_wa: 'QLD/WA',
  price_venue_per_litre: 'Venue/L',
  price_pint: 'Pint',
  price_half_pint: 'Half Pint',
  price_150ml: '150ml',
  price_500ml: '500ml',
  price_bar: 'O.P.',
};

const ROUNDING_LABELS = {
  ceil_50: 'round up to 50c',
  nearest_50: 'nearest 50c',
  floor_50: 'round down to 50c',
  nearest_cent: 'nearest cent',
};

function fieldLabel(fr) {
  const field = fr.rowKind === 'kounta_line' ? fr.sell?.field : fr.field;
  return fr.label || FIELD_LABELS[field] || field || '';
}

function guideTooltip(guide) {
  if (!guide) return '';
  if (!guide.available || guide.value == null) return guide.reason || 'No Guide target for this field';
  const bits = [];
  if (guide.reason) bits.push(guide.reason);
  if (guide.targetPct != null) bits.push(`${guide.targetPct}% GP target`);
  if (guide.rounding) bits.push(ROUNDING_LABELS[guide.rounding] || guide.rounding);
  return bits.join(', ') || 'Pricing Guide expected value';
}

function shopifyTooltip(store, cell, compared) {
  const lines = [];
  if (cell?.variantTitle) lines.push(`${store} variant: ${cell.variantTitle}`);
  if (cell?.expected != null) {
    const formula = cell.formulaLabel ? ` (${cell.formulaLabel})` : '';
    lines.push(`Expected ${formatMoney(cell.expected)}${formula}`);
  }
  if (
    cell?.guideExpected != null
    && cell.guideMatch === false
  ) {
    lines.push(`Guide formula ${formatMoney(cell.guideExpected)}`);
  }
  if (!compared) lines.push('Not compared (read-only store)');
  else if (cell?.match === false) lines.push('Mismatch');
  else if (cell?.match === true) lines.push('Matches expected');
  return lines.join(' · ');
}

/**
 * Per-SKU resolve table:
 * Field | Guide | Matrix | Monday | Cin7 | Shopify | Kounta sell | Overwrite.
 */
export default function SkuFieldTable({
  rows,
  selections,
  setSelections,
  overrides,
  setOverrides,
  disabled,
}) {
  const clearField = (field) => {
    setOverrides((prev) => {
      const o = { ...prev };
      delete o[field];
      return o;
    });
    setSelections((p) => {
      const n = { ...p };
      delete n[field];
      return n;
    });
  };

  const select = (field, source) => {
    setOverrides((prev) => {
      const o = { ...prev };
      delete o[field];
      return o;
    });
    setSelections((p) => ({ ...p, [field]: source }));
  };

  const radioCell = (s, source, value, opts = {}) => {
    if (!s || !opts.available) return <Text type="secondary">—</Text>;
    const checked = selections[s.field] === source;
    const radio = (
      <Radio
        checked={checked}
        disabled={disabled || opts.disabled}
        onClick={(e) => {
          if (!checked) return;
          e.preventDefault();
          clearField(s.field);
        }}
        onChange={() => {
          if (checked) return;
          select(s.field, source);
        }}
      >
        {formatMoney(value)}
      </Radio>
    );
    if (!opts.tooltip) return radio;
    return <Tooltip title={opts.tooltip}>{radio}</Tooltip>;
  };

  const columns = [
    {
      title: 'Field / Kounta line',
      key: 'label',
      width: 260,
      ellipsis: true,
      render: (_, fr) => {
        const label = fieldLabel(fr);
        if (fr.rowKind !== 'kounta_line') {
          return (
            <span>
              {label}
              {fr.issueType && ISSUE_META[fr.issueType] ? (
                <Tag color={ISSUE_META[fr.issueType].tag} style={{ marginLeft: 6 }}>
                  {ISSUE_META[fr.issueType].label}
                </Tag>
              ) : null}
            </span>
          );
        }
        const sellField = fr.sell?.field;
        return (
          <span>
            {label}
            {sellField ? (
              <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                {FIELD_LABELS[sellField] || sellField}
              </Text>
            ) : null}
            {fr.issueType && ISSUE_META[fr.issueType] ? (
              <Tag color={ISSUE_META[fr.issueType].tag} style={{ marginLeft: 6 }}>
                {ISSUE_META[fr.issueType].label}
              </Tag>
            ) : null}
          </span>
        );
      },
    },
    {
      title: 'Guide',
      key: 'guide',
      width: 120,
      render: (_, fr) => {
        const s = getEditableSlot(fr);
        if (!s) return <Text type="secondary">—</Text>;
        const g = s.guide;
        if (!g?.available || g.value == null) {
          return (
            <Tooltip title={guideTooltip(g)}>
              <Text type="secondary">—</Text>
            </Tooltip>
          );
        }
        return radioCell(s, 'guide', g.value, { available: true, tooltip: guideTooltip(g) });
      },
    },
    {
      title: 'Matrix',
      key: 'matrix',
      width: 110,
      render: (_, fr) => {
        const s = getEditableSlot(fr);
        return radioCell(s, 'matrix', s?.matrix?.value, { available: Boolean(s) });
      },
    },
    {
      title: 'Monday',
      key: 'monday',
      width: 110,
      render: (_, fr) => {
        const s = getEditableSlot(fr);
        return radioCell(s, 'monday', s?.monday?.value, { available: Boolean(s?.monday?.available) });
      },
    },
    {
      title: 'Cin7',
      key: 'cin7',
      width: 110,
      render: (_, fr) => {
        const s = getEditableSlot(fr);
        return radioCell(s, 'cin7', s?.cin7?.value, {
          available: Boolean(s?.cin7?.available),
          disabled: !s?.cin7?.reversible,
          tooltip: s?.cin7?.purpose ? `Tier ${s.cin7.tierNum} — ${s.cin7.purpose}` : undefined,
        });
      },
    },
    {
      title: 'Shopify',
      key: 'shopify',
      width: 180,
      render: (_, fr) => {
        const s = getEditableSlot(fr);
        const gpnz = getStore(s?.shopify, SHOPIFY_COMPARED_STORE);
        const gpau = getStore(s?.shopify, SHOPIFY_READONLY_STORE);
        if (!gpnz?.available && !gpau?.available) return <Text type="secondary">—</Text>;
        const mismatch = gpnz?.available && gpnz.match === false;
        const canSelect = Boolean(s && gpnz?.selectableSource && gpnz.value != null);
        let nzNode = <Text type="secondary">—</Text>;
        if (canSelect) {
          nzNode = radioCell(s, 'shopify', gpnz.value, {
            available: true,
            tooltip: shopifyTooltip(SHOPIFY_COMPARED_STORE, gpnz, true),
          });
        } else if (gpnz?.available) {
          nzNode = (
            <Tooltip title={shopifyTooltip(SHOPIFY_COMPARED_STORE, gpnz, true)}>
              <Text type={mismatch ? 'danger' : undefined} strong={mismatch}>
                {formatMoney(gpnz.value)}
              </Text>
            </Tooltip>
          );
        }
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {nzNode}
            {gpau?.available ? (
              <Tooltip title={shopifyTooltip(SHOPIFY_READONLY_STORE, gpau, false)}>
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                  AU
                  {' '}
                  {formatMoney(gpau.value)}
                </Text>
              </Tooltip>
            ) : null}
          </span>
        );
      },
    },
    {
      title: 'Kounta sell',
      key: 'kounta',
      width: 120,
      render: (_, fr) => {
        if (fr.rowKind !== 'kounta_line') return <Text type="secondary">—</Text>;
        const sell = fr.kountaLine?.sell;
        const s = getEditableSlot(fr);
        if (!sell || sell.applicable === false) return <Text type="secondary">—</Text>;
        const canSelect = Boolean(s && sell.actual != null);
        if (canSelect) {
          const expected = sell.expected != null ? `Matrix field expected ${formatMoney(sell.expected)}` : '';
          return radioCell(s, 'kounta', sell.actual, {
            available: true,
            tooltip: expected || 'Kounta sell price',
          });
        }
        const bad = sell.match === false;
        return (
          <Tooltip title={sell.expected != null ? `Expected ${formatMoney(sell.expected)}` : ''}>
            <Text type={bad ? 'danger' : undefined}>{formatMoney(sell.actual)}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Overwrite',
      key: 'overwrite',
      width: 120,
      render: (_, fr) => {
        const s = getEditableSlot(fr);
        if (!s) return <Text type="secondary">—</Text>;
        return (
          <InputNumber
            size="small"
            style={{ width: 105 }}
            min={0}
            step={0.5}
            placeholder="New price"
            disabled={disabled}
            value={overrides[s.field] ?? null}
            onChange={(val) => {
              if (val === null || val === undefined) {
                clearField(s.field);
                return;
              }
              setOverrides((prev) => ({ ...prev, [s.field]: val }));
              setSelections((p) => ({ ...p, [s.field]: 'override' }));
            }}
          />
        );
      },
    },
    {
      title: '',
      key: 'clear',
      width: 64,
      render: (_, fr) => {
        const s = getEditableSlot(fr);
        if (!s || selections[s.field] == null) return null;
        return (
          <Button
            type="link"
            size="small"
            disabled={disabled}
            style={{ padding: 0 }}
            onClick={() => clearField(s.field)}
          >
            Clear
          </Button>
        );
      },
    },
  ];

  return (
    <Table
      size="small"
      pagination={false}
      dataSource={rows}
      rowKey={(r) => r.rowKey || r.field}
      scroll={{ x: 1200 }}
      columns={columns}
      onRow={(fr) => {
        const tint = ISSUE_META[fr.issueType]?.row;
        if (tint) return { style: { background: tint } };
        if (rowHasDiscrepancy(fr)) return { style: { background: '#fff1f0' } };
        return {};
      }}
    />
  );
}

const cellShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  available: PropTypes.bool,
  shopifyVariantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const rowShape = PropTypes.shape({
  rowKind: PropTypes.string,
  rowKey: PropTypes.string,
  field: PropTypes.string,
  label: PropTypes.string,
  guide: cellShape,
  matrix: cellShape,
  monday: cellShape,
  cin7: cellShape,
  shopify: PropTypes.shape({}),
  sell: PropTypes.shape({}),
  kountaLine: PropTypes.shape({}),
  hasDiscrepancy: PropTypes.bool,
  hasMonCinDiscrepancy: PropTypes.bool,
});

SkuFieldTable.propTypes = {
  rows: PropTypes.arrayOf(rowShape).isRequired,
  /** field -> 'guide' | 'matrix' | 'monday' | 'cin7' | 'shopify' | 'kounta' | 'override' */
  selections: PropTypes.objectOf(PropTypes.string).isRequired,
  setSelections: PropTypes.func.isRequired,
  /** field -> numeric override when source is 'override' */
  overrides: PropTypes.objectOf(PropTypes.number).isRequired,
  setOverrides: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SkuFieldTable.defaultProps = {
  disabled: false,
};
